import React, { useEffect, useMemo, useState } from "react";
import i18n from "@/utils/i18n";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Modal from "@/components/ui/modal";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import { Close as CloseIcon } from "@/components/icons";
import { certificatesApi } from "../../services/certificates.api";
import type { DomainSettingsDto } from "../../services/certificates.api";
import styles from "./CertificateNumberSettingsModal.module.scss";

interface CertificateNumberSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Kaydetme başarılıysa çağrılır (ör. liste tazelemesi için) */
  onSaved?: (settings: DomainSettingsDto) => void;
  /** DOMAIN_SETTINGS.UPDATE yetkisi yoksa input + aksiyonlar salt-okunur olur. */
  canUpdate?: boolean;
}

/** Pattern'i canlı önizleme için mevcut tarihe göre render eder.
 *  Sequence yer tutucusunu (000001, 0001 gibi) göstermelik değerle doldurur. */
function previewPattern(pattern: string): string {
  const d = new Date();
  const year = String(d.getFullYear());
  const year2 = year.slice(-2);
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const domain = "DOMAIN-UUID";
  return pattern
    .replace(/\{YEAR:2\}/g, year2)
    .replace(/\{YEAR\}/g, year)
    .replace(/\{MONTH\}/g, month)
    .replace(/\{DAY\}/g, day)
    .replace(/\{DOMAIN\}/g, domain)
    .replace(/\{SEQ:(\d{1,2})\}/g, (_m, n) => {
      const width = Math.min(12, Math.max(1, Number(n) || 1));
      return "42".padStart(width, "0");
    });
}

/** Pattern'in client-side ön doğrulaması. Bilinmeyen token veya geçersiz
 *  SEQ:N → hata mesajı. BE zaten kontrol ediyor; bu UI hızlı feedback için. */
function validatePatternClientSide(pattern: string): string | null {
  if (!pattern) return null; // boş → varsayılana düşecek
  if (pattern.length > 200)
    return "Pattern en fazla 200 karakter olabilir.";
  const tokenRegex = /\{([^}]+)\}/g;
  const allowed = new Set(["YEAR", "YEAR:2", "MONTH", "DAY", "DOMAIN"]);
  let match: RegExpExecArray | null;
  while ((match = tokenRegex.exec(pattern)) !== null) {
    const content = match[1];
    if (allowed.has(content)) continue;
    const seq = /^SEQ:(\d{1,2})$/.exec(content);
    if (seq) {
      const n = Number(seq[1]);
      if (n < 1 || n > 12) return `{SEQ:${seq[1]}} geçersiz. 1–12 arası olmalı.`;
      continue;
    }
    return `Bilinmeyen token: {${content}}. Desteklenenler: {YEAR}, {YEAR:2}, {MONTH}, {DAY}, {SEQ:N}, {DOMAIN}.`;
  }
  return null;
}

const EXAMPLES = [
  { pattern: "DOCUART-{YEAR}-{SEQ:6}", output: "DOCUART-2026-000042" },
  {
    pattern: "{YEAR}{MONTH}{DAY}-{SEQ:4}",
    output: "20260419-0042",
    isDefault: true,
  },
  { pattern: "CERT/{YEAR:2}/{SEQ:5}", output: "CERT/26/00042" },
];

const CertificateNumberSettingsModal: React.FC<
  CertificateNumberSettingsModalProps
> = ({ isOpen, onClose, onSaved, canUpdate = true }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<DomainSettingsDto | null>(null);
  const [pattern, setPattern] = useState("");
  const [maxExamFailureCount, setMaxExamFailureCount] = useState<string>("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [maxExamFailureCountError, setMaxExamFailureCountError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setServerError(null);
    setValidationError(null);
    certificatesApi
      .getDomainSettings()
      .then((s) => {
        setSettings(s);
        setPattern(s.certificateNumberPattern ?? "");
        setMaxExamFailureCount(
          typeof s.maxExamFailureCount === "number" ? String(s.maxExamFailureCount) : "3"
        );
      })
      .catch(() => {
        toast.error(
          t("certificates.numberSettings.loadError"),
        );
      })
      .finally(() => setLoading(false));
  }, [isOpen, t]);

  const activePattern =
    pattern.trim() ||
    settings?.defaultCertificateNumberPattern ||
    "{YEAR}{MONTH}{DAY}-{SEQ:4}";

  const preview = useMemo(() => previewPattern(activePattern), [activePattern]);

  const usingDefault = pattern.trim() === "";

  const handlePatternChange = (value: string) => {
    setPattern(value);
    setServerError(null);
    const err = validatePatternClientSide(value);
    setValidationError(err);
  };

  const handleSave = async () => {
    if (!canUpdate) return;
    if (validationError) {
      toast.error(validationError);
      return;
    }
    if (maxExamFailureCountError) {
      toast.error(maxExamFailureCountError);
      return;
    }
    const parsedMaxExamFailureCount = Number(maxExamFailureCount);
    if (
      !Number.isInteger(parsedMaxExamFailureCount) ||
      parsedMaxExamFailureCount < 1 ||
      parsedMaxExamFailureCount > 20
    ) {
      const msg = t("certificates.numberSettings.maxExamFailureCountInvalid");
      setMaxExamFailureCountError(msg);
      toast.error(msg);
      return;
    }
    setSaving(true);
    setServerError(null);
    try {
      // Boş input → null gönder (varsayılana dön)
      const body = {
        certificateNumberPattern: pattern.trim() === "" ? null : pattern.trim(),
        maxExamFailureCount: parsedMaxExamFailureCount,
      };
      const updated = await certificatesApi.updateDomainSettings(body);
      setSettings(updated);
      setPattern(updated.certificateNumberPattern ?? "");
      toast.success(
        t("certificates.numberSettings.saveSuccess"),
      );
      onSaved?.(updated);
      onClose();
    } catch (err: unknown) {
      const statusCode = (err as { response?: { status?: number } })?.response
        ?.status;
      const message = (
        err as { response?: { data?: { message?: string } } }
      )?.response?.data?.message;
      if (statusCode === 400) {
        setServerError(
          message ??
            i18n.t("certificateNumberSettings.invalidTemplate"),
        );
      } else {
        toast.error(
          message ??
            t("certificates.numberSettings.saveError"),
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = () => {
    setPattern("");
    setValidationError(null);
    setServerError(null);
  };

  const handleMaxExamFailureCountChange = (value: string) => {
    setMaxExamFailureCount(value);
    setServerError(null);
    if (value.trim() === "") {
      setMaxExamFailureCountError(
        t("certificates.numberSettings.maxExamFailureCountRequired")
      );
      return;
    }
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > 20) {
      setMaxExamFailureCountError(
        t("certificates.numberSettings.maxExamFailureCountInvalid")
      );
      return;
    }
    setMaxExamFailureCountError(null);
  };

  const applyExample = (value: string) => {
    setPattern(value);
    const err = validatePatternClientSide(value);
    setValidationError(err);
    setServerError(null);
  };

  const customHeader = (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "16px 24px",
        borderBottom: "1px solid #e8e8e9",
      }}
    >
      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "#14181f" }}>
        {t("certificates.numberSettings.title")}
      </h3>
      <button
        type="button"
        onClick={onClose}
        style={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: 4,
        }}
        aria-label={t("buttons.close")}
      >
        <CloseIcon width={20} height={20} color="#72808f" />
      </button>
    </div>
  );

  const customFooter = (
    <div className={styles.footer}>
      <Button
        variant="text"
        color="secondary"
        onClick={handleResetToDefault}
        disabled={saving || loading || usingDefault || !canUpdate}
        title={
          canUpdate
            ? t("certificates.numberSettings.resetHint")
            : t("certificates.numberSettings.readOnly")
        }
      >
        {t("certificates.numberSettings.resetToDefault")}
      </Button>
      <div className={styles.footerRight}>
        <Button
          variant="outlined"
          color="default"
          onClick={onClose}
          disabled={saving}
        >
          {t("buttons.cancel", canUpdate ? t("buttons.cancel") : t("certificateNumberSettings.close"))}
        </Button>
        {canUpdate && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving || loading || !!validationError || !!maxExamFailureCountError}
            >
            {saving
              ? t("common.loading")
              : t("buttons.save")}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("certificates.numberSettings.title")}
      customHeader={customHeader}
      customFooter={customFooter}
      size="lg"
    >
      <div className={styles.modalBody}>
        {loading ? (
          <div>{t("common.loading")}</div>
        ) : (
          <>
            {!canUpdate && (
              <div className={styles.errorBox} style={{ background: "#f8f8f9", borderColor: "#dadfe3", color: "#707a8f" }}>
                {t("certificates.numberSettings.readOnlyHint")}
              </div>
            )}
            {/* Pattern input */}
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>
                {t("certificates.numberSettings.patternLabel")}
              </h4>
              <p className={styles.hint}>
                {t("certificates.numberSettings.patternHint")}
              </p>
              <Input
                value={pattern}
                onChange={(e) => handlePatternChange(e.target.value)}
                placeholder={
                  settings?.defaultCertificateNumberPattern ||
                  "{YEAR}{MONTH}{DAY}-{SEQ:4}"
                }
                maxLength={200}
                className={styles.patternInput}
                error={!!validationError}
                errorMessage={validationError ?? undefined}
                disabled={!canUpdate}
              />
              {serverError && (
                <div className={styles.errorBox}>{serverError}</div>
              )}
            </div>

            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>
                {t("certificates.numberSettings.maxExamFailureCountLabel")}
              </h4>
              <p className={styles.hint}>
                {t("certificates.numberSettings.maxExamFailureCountHint")}
              </p>
              <Input
                value={maxExamFailureCount}
                onChange={(e) => handleMaxExamFailureCountChange(e.target.value)}
                type="number"
                min={1}
                max={20}
                step={1}
                className={styles.patternInput}
                error={!!maxExamFailureCountError}
                errorMessage={maxExamFailureCountError ?? undefined}
                disabled={!canUpdate}
              />
            </div>

            {/* Preview */}
            <div className={styles.previewBox}>
              <span className={styles.previewLabel}>
                {usingDefault
                  ? t("certificates.numberSettings.previewDefaultLabel")
                  : t("certificates.numberSettings.previewLabel")}
              </span>
              <span className={styles.previewValue}>{preview}</span>
            </div>

            {/* Token reference */}
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>
                {t("certificates.numberSettings.tokensLabel")}
              </h4>
              <table className={styles.tokenTable}>
                <thead>
                  <tr>
                    <th>Token</th>
                    <th>{t("common.description")}</th>
                    <th>{t("certificates.numberSettings.exampleHeader")}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <code>{"{YEAR}"}</code>
                    </td>
                    <td>{t("certificates.numberSettings.tokenYear4")}</td>
                    <td>2026</td>
                  </tr>
                  <tr>
                    <td>
                      <code>{"{YEAR:2}"}</code>
                    </td>
                    <td>{t("certificates.numberSettings.tokenYear2")}</td>
                    <td>26</td>
                  </tr>
                  <tr>
                    <td>
                      <code>{"{MONTH}"}</code>
                    </td>
                    <td>2 haneli ay</td>
                    <td>04</td>
                  </tr>
                  <tr>
                    <td>
                      <code>{"{DAY}"}</code>
                    </td>
                    <td>{t("certificates.numberSettings.tokenDay2")}</td>
                    <td>19</td>
                  </tr>
                  <tr>
                    <td>
                      <code>{"{SEQ:N}"}</code>
                    </td>
                    <td>{t("certificates.numberSettings.tokenSeqN")}</td>
                    <td>{"{SEQ:6} → 000042"}</td>
                  </tr>
                  <tr>
                    <td>
                      <code>{"{DOMAIN}"}</code>
                    </td>
                    <td>Domain UUID</td>
                    <td>—</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Examples */}
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>
                {t("certificates.numberSettings.examplesLabel")}
              </h4>
              <div className={styles.exampleGrid}>
                {EXAMPLES.map((ex) => (
                  <button
                    key={ex.pattern}
                    type="button"
                    onClick={() => applyExample(ex.pattern)}
                    className={styles.exampleCard}
                    disabled={!canUpdate}
                  >
                    <code className={styles.exampleCode}>
                      {ex.pattern}
                      {ex.isDefault && (
                        <span className={styles.exampleDefaultBadge}>
                          (varsayılan)
                        </span>
                      )}
                    </code>
                    <span className={styles.exampleOutput}>→ {ex.output}</span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default CertificateNumberSettingsModal;
